import { useState, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Property } from '@/data/properties';
import { usePrefersReducedMotion, useIsMobileWidth } from '@/hooks/useMediaQuery';

interface PropertyHeroProps {
  property: Property;
}

export const PropertyHero = ({ property }: PropertyHeroProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Accessibility and mobile hooks
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobileWidth(768);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  // Handle video error - fall back to image
  const handleVideoError = () => {
    console.warn('Property hero video failed to load, falling back to image');
    setVideoError(true);
  };

  // Check if property has video and determine if we should show it
  const hasVideo = !!(property.heroVideoMp4 || property.heroVideoWebm);
  const shouldShowVideo = hasVideo && !videoError && !prefersReducedMotion && !isMobile;

  // Get the poster/fallback image
  const posterImage = property.heroPosterImage || property.images[0];
  const currentImage = property.images[currentImageIndex];

  return (
    <section className="relative h-[60vh] lg:h-[70vh] overflow-hidden pt-20">
      {/* Background Media (Video or Image) */}
      <div className="absolute inset-0">
        {shouldShowVideo ? (
          // Video Background
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={posterImage}
            className="w-full h-full object-cover"
            onError={handleVideoError}
          >
            {property.heroVideoWebm && (
              <source src={property.heroVideoWebm} type="video/webm" />
            )}
            {property.heroVideoMp4 && (
              <source src={property.heroVideoMp4} type="video/mp4" />
            )}
            {/* Fallback for browsers that don't support video */}
            <img
              src={posterImage}
              alt={`${property.name} interior view`}
              className="w-full h-full object-cover"
            />
          </video>
        ) : (
          // Static Image Background (with carousel support when no video)
          <img
            src={hasVideo ? posterImage : currentImage}
            alt={`${property.name} interior view`}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Image Navigation - Only show when not using video */}
      {!shouldShowVideo && property.images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Image Indicators - Only show when not using video */}
      {!shouldShowVideo && property.images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {property.images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentImageIndex ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Content Overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 lg:p-8">
        <div className="container mx-auto">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge variant="secondary" className="text-sm">
                {property.type}
              </Badge>
              {property.hasVirtualTour && (
                <Badge variant="outline" className="text-sm border-white text-white">
                  ✨ 3D Tour Available
                </Badge>
              )}
              <Badge variant="outline" className="text-sm border-white text-white">
                {property.viewDirection} View
              </Badge>
            </div>
            
            <h1 className="font-display text-4xl lg:text-6xl font-bold text-white mb-4">
              {property.name}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-white/90 text-lg">
              <span>{property.sqft} sq ft</span>
              <span>•</span>
              <span>{property.beds} bed{property.beds > 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{property.baths} bath{property.baths > 1 ? 's' : ''}</span>
              {property.priceRange && (
                <>
                  <span>•</span>
                  <span className="font-semibold text-xl">{property.priceRange}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
